import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { Backdrop } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment";
import TabHeader from "src/components/TabHeader/TabHeader";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import { useStyles as useCommonGridStyles } from "src/components/CommonXGrid/styles";
import CommonLoader from "src/components/Loader/CommonLoader";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import {
  selectAllPartyNameRowsList,
  selectAllPaymentTypeRowsList,
} from "src/app-redux/configuration/configurationSlice";
import {
  getAllPartName,
  getAllPaymentTypes,
} from "src/app-redux/configuration/actions/configurationActions";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import { YardRoute } from "../../yard.routes";
import { useStyles } from "../container/components/style";
import { ExamDialog, ExamDialogChargeOption, ExamDialogPayload } from "./ExamDialog";
import type { ContainerExaminationRow, PaymentType } from "./types";

const CONTAINER_EXAM_ENDPOINT = "get-container-examinatios";
const CREATE_EXAM_ENDPOINT = "create-container-examination";
const DELETE_EXAM_ENDPOINT = "delete-container-examinations";
const UPDATE_EXAM_ENDPOINT = "update-container-examination";

/** API uses 0001-01-01 when there is no real date */
function normalizeExamDate(value: unknown): string {
  if (value == null || value === "") return "";
  const s = String(value);
  if (s.startsWith("0001-01-01")) return "";
  return s;
}

function latestExaminationEntry(history: unknown): Record<string, unknown> | null {
  if (!Array.isArray(history) || history.length === 0) return null;
  return history[history.length - 1] as Record<string, unknown>;
}

function hasAnyCharges(row: any): boolean {
  const fork = Number(row?.forkLifter ?? 0);
  const lab = Number(row?.labour ?? 0);
  if ((Number.isFinite(fork) && fork > 0) || (Number.isFinite(lab) && lab > 0)) return true;

  const hist = row?.examinationHistory;
  if (!Array.isArray(hist) || hist.length === 0) return false;
  return hist.some(
    (h: any) => Number(h?.lifterCharges ?? 0) > 0 || Number(h?.labourCharges ?? 0) > 0
  );
}

function toNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(String(value ?? "").trim());
  return Number.isFinite(n) ? n : 0;
}

function sumHistoryCharges(history: unknown): { labour: number; lifter: number } | null {
  if (!Array.isArray(history) || history.length === 0) return null;
  let labour = 0;
  let lifter = 0;
  for (const h of history as any[]) {
    labour += toNumber(h?.labourCharges ?? h?.labourCharge);
    lifter += toNumber(h?.lifterCharges ?? h?.forkLifterCharge ?? h?.forkLifterCharges);
  }
  return { labour, lifter };
}

function asId(pt: PaymentType): number | null {
  const raw = pt?.id ?? pt?.paymentTypeId;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function asAmount(pt: PaymentType): number {
  const n = Number((pt as any)?.amount ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export function ContainerExamination(): React.ReactElement {
  const classes = useStyles();
  const gridClasses: any = useCommonGridStyles();
  const dispatch = useAppDispatch();

  const moduleName = YardRoute.title;
  const pageHeading = "Container Examination";

  const partyRows: any[] = useAppSelector(selectAllPartyNameRowsList) as any;
  const paymentTypeRows: any[] = useAppSelector(selectAllPaymentTypeRowsList) as any;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ContainerExaminationRow[]>([]);

  const [forwarderId, setForwarderId] = useState<string>("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"exam" | "re-exam">("exam");
  const [dialogContainerNo, setDialogContainerNo] = useState<string>("");
  const [dialogContainerSize, setDialogContainerSize] = useState<string>("");
  const [dialogGatePassId, setDialogGatePassId] = useState<number | null>(null);
  const [dialogExistingExamDate, setDialogExistingExamDate] = useState<string>("");
  const [saving, setSaving] = useState(false);

  type PercentageOption = { label: string; value: number };
  const PERCENTAGE_OPTIONS: PercentageOption[] = useMemo(
    () => [
      { label: "25%", value: 25 },
      { label: "50%", value: 50 },
      { label: "75%", value: 75 },
      { label: "100%", value: 100 },
    ],
    []
  );

  const [editOpen, setEditOpen] = useState(false);
  const [editGatePassId, setEditGatePassId] = useState<number | null>(null);
  const [editExamId, setEditExamId] = useState<number | null>(null);
  const [editExamDate, setEditExamDate] = useState<string>("");
  const [editReExamDate, setEditReExamDate] = useState<string>("");
  const [editLifterPct, setEditLifterPct] = useState<PercentageOption | null>(null);
  const [editLabourPct, setEditLabourPct] = useState<PercentageOption | null>(null);
  const [editLifterCharges, setEditLifterCharges] = useState<string>("0");
  const [editLabourCharges, setEditLabourCharges] = useState<string>("0");
  const [editRemarks, setEditRemarks] = useState<string>("");

  const [remarksByExamId, setRemarksByExamId] = useState<Record<string, string>>({});

  const formatRs = (value: any): string => {
    if (value == null || value === "" || value === "-") return "-";
    const n = toNumber(value);
    if (!Number.isFinite(n)) return "-";
    return `Rs ${n}`;
  };

  const calcAmount = (base: number, pct: number) => {
    const b = Number(base || 0);
    const p = Number(pct || 0);
    const v = (b * p) / 100;
    return Math.round(v * 100) / 100;
  };

  function ReExamDateCell({ cell }: any) {
    return cell.getValue() ? moment(cell.getValue()).format("DD-MM-YYYY") : "-";
  }
  ReExamDateCell.displayName = "ReExamDateCell";

  function ActionCell({ row }: any) {
    const containerNo = row?.original?.containerNo || row?.original?.containerNumber || "";
    const gatePassId = row?.original?.gatePassId ?? null;
    const containerSize = row?.original?.containerSize ?? row?.original?.container_size ?? "";
    const latestExam = latestExaminationEntry(row?.original?.examinationHistory);
    const existingExamDate = normalizeExamDate((latestExam as any)?.examDate);
    const chargesExist = hasAnyCharges(row?.original);
    return (
      <Box style={{ display: "flex", gap: 8 }}>
        {!chargesExist ? (
          <button
            className="MuiButtonBase-root MuiButton-root MuiButton-contained MuiButton-containedPrimary MuiButton-sizeSmall"
            style={{
              backgroundColor: "#72CA00",
              color: "#fff",
              border: "none",
              padding: "6px 10px",
              cursor: "pointer",
              borderRadius: 4,
            }}
            onClick={() => {
              setDialogMode("exam");
              setDialogContainerNo(String(containerNo || ""));
              setDialogContainerSize(String(containerSize || ""));
              setDialogGatePassId(gatePassId ? Number(gatePassId) : null);
              setDialogExistingExamDate(existingExamDate || "");
              setDialogOpen(true);
            }}
          >
            Exam
          </button>
        ) : (
          <button
            className="MuiButtonBase-root MuiButton-root MuiButton-contained MuiButton-containedSecondary MuiButton-sizeSmall"
            style={{
              backgroundColor: "#72CA00",
              color: "#fff",
              border: "none",
              padding: "6px 10px",
              cursor: "pointer",
              borderRadius: 4,
            }}
            onClick={() => {
              setDialogMode("re-exam");
              setDialogContainerNo(String(containerNo || ""));
              setDialogContainerSize(String(containerSize || ""));
              setDialogGatePassId(gatePassId ? Number(gatePassId) : null);
              setDialogExistingExamDate(existingExamDate || "");
              setDialogOpen(true);
            }}
          >
            Re-Exam
          </button>
        )}
      </Box>
    );
  }
  ActionCell.displayName = "ContainerExaminationActionCell";

  function ExamDateCell({ cell }: any) {
    return cell.getValue() ? moment(cell.getValue()).format("DD-MM-YYYY") : "-";
  }
  ExamDateCell.displayName = "ExamDateCell";

  function SrNoCell({ row }: any) {
    return row?.index != null ? Number(row.index) + 1 : "-";
  }
  SrNoCell.displayName = "SrNoCell";

  useEffect(() => {
    dispatch(getAllPartName(true));
    dispatch(getAllPaymentTypes());
  }, [dispatch]);

  const chargeOptions: ExamDialogChargeOption[] = useMemo(() => {
    const list = (paymentTypeRows || []) as PaymentType[];
    return (list || [])
      .map((pt) => ({
        id: asId(pt),
        name: String(pt?.name || "").trim(),
        amount: asAmount(pt),
      }))
      .filter((x) => x.id && x.name) as any;
  }, [paymentTypeRows]);

  const chargeBaseAmounts = useMemo(() => {
    const labour = (chargeOptions || []).find((c) =>
      String(c?.name || "")
        .toLowerCase()
        .includes("labour")
    );
    const lifter = (chargeOptions || []).find((c) => {
      const name = String(c?.name || "").toLowerCase();
      return name.includes("fork") || name.includes("lifter");
    });
    return {
      labour: toNumber(labour?.amount ?? 0),
      lifter: toNumber(lifter?.amount ?? 0),
    };
  }, [chargeOptions]);

  const columns = useMemo(
    () => [
      { accessorKey: "serialNumber", header: "Sr.No", size: 70, Cell: SrNoCell },
      { accessorKey: "containerNo", header: "Container No", size: 120 },
      { accessorKey: "containerSize", header: "Container Size", size: 130 },
      { accessorKey: "cargoType", header: "Cargo Type", size: 150 },
      { accessorKey: "importer", header: "Importer", size: 220 },
      {
        accessorKey: "forkLifter",
        header: "Fork Lifter",
        size: 120,
        Cell: ({ cell }: any) => formatRs(cell.getValue()),
      },
      {
        accessorKey: "labour",
        header: "Labour",
        size: 120,
        Cell: ({ cell }: any) => formatRs(cell.getValue()),
      },
      {
        accessorKey: "totalCharges",
        header: "Total Charges",
        size: 130,
        Cell: ({ cell }: any) => formatRs(cell.getValue()),
      },
      {
        id: "action",
        header: "Action",
        size: 160,
        Cell: ActionCell,
      },
    ],
    []
  );

  const detailPanel = (row: any) => {
    const original = row?.row?.original;
    const history = original?.examinationHistory;
    if (!Array.isArray(history) || history.length === 0) return null;
    if (Number(original?.isExamDone ?? 0) !== 1) return null;

    const percent = (v: any) => {
      if (v == null) return "-";
      const s = String(v).trim();
      if (!s) return "-";
      if (s.endsWith("%")) return s; // API sometimes returns "25%"
      const n = Number(s);
      if (Number.isFinite(n)) return `${n}%`;
      const f = Number.parseFloat(s);
      // If it's some other non-empty format, show it instead of hiding it.
      return Number.isFinite(f) ? `${f}%` : s;
    };

    const pickPercentValue = (obj: any, candidates: string[]) => {
      for (const k of candidates) {
        const v = obj?.[k];
        if (v !== undefined && v !== null && String(v).trim() !== "") return v;
      }
      // Fallback: find any key that looks like fork/lift + percent/pct
      const key = Object.keys(obj || {}).find((kk) =>
        /(fork|lift|lifter).*(percent|percentage|percen|precent|pct)/i.test(kk)
      );
      if (key) return obj?.[key];

      // Last resort: some APIs send only one non-labour percent field
      const anyNonLabour = Object.keys(obj || {}).find(
        (kk) => /(percent|percentage|percen|precent|pct)/i.test(kk) && !/labou?r/i.test(kk)
      );
      if (anyNonLabour) return obj?.[anyNonLabour];
      return undefined;
    };

    const baseAmountFor = (which: "labour" | "lifter"): number => {
      const list = (chargeOptions || []) as Array<{ name?: string; amount?: number }>;
      const found = list.find((c) => {
        const name = String(c?.name || "").toLowerCase();
        if (which === "labour") return name.includes("labour");
        return name.includes("fork") || name.includes("lifter");
      });
      return toNumber(found?.amount ?? 0);
    };

    // Fallback when API does not return percent fields: infer applied % from
    // (calculated charge amount / configured base amount) * 100.
    const percentFromBaseAmount = (h: any, which: "labour" | "lifter"): string | null => {
      const base = baseAmountFor(which);
      if (!Number.isFinite(base) || base <= 0) return null;

      const amount =
        which === "labour"
          ? toNumber(h?.labourCharges ?? h?.labourCharge)
          : toNumber(h?.lifterCharges ?? h?.forkLifterCharge ?? h?.forkLifterCharges);

      if (!Number.isFinite(amount) || amount < 0) return null;
      const p = (amount / base) * 100;
      if (!Number.isFinite(p)) return null;

      // Keep integer-like display to match existing UI (25/50/75/100)
      return `${Math.round(p)}%`;
    };

    const pickRemarks = (obj: any): string => {
      if (!obj || typeof obj !== "object") return "";

      const direct =
        (obj as any)?.remarks ??
        (obj as any)?.remark ??
        (obj as any)?.Remarks ??
        (obj as any)?.Remark ??
        (obj as any)?.remarksText ??
        (obj as any)?.remarksNote ??
        (obj as any)?.note ??
        (obj as any)?.notes;

      const directStr = direct != null ? String(direct).trim() : "";
      if (directStr) return directStr;

      // API key varies a lot; pick any non-empty field containing "remark"
      for (const k of Object.keys(obj)) {
        if (!/remark/i.test(k)) continue;
        const v = (obj as any)[k];
        const s = v != null ? String(v).trim() : "";
        if (s) return s;
      }
      return "";
    };

    const openEditDialog = (h: any) => {
      const gatePassId = original?.gatePassId ?? null;
      const examId = h?.examId ?? h?.examID ?? h?.id ?? null;
      setEditGatePassId(gatePassId != null ? Number(gatePassId) : null);
      setEditExamId(examId != null ? Number(examId) : null);

      const toInputDate = (v: any) => {
        const normalized = normalizeExamDate(v);
        if (!normalized) return "";
        const m = moment(normalized);
        return m.isValid() ? m.format("YYYY-MM-DDTHH:mm") : "";
      };

      setEditExamDate(toInputDate(h?.examDate));
      setEditReExamDate(toInputDate(h?.reExamDate ?? h?.reExamOn));

      const pctFrom = (raw: any): number | null => {
        if (raw == null) return null;
        const s = String(raw).trim();
        if (!s) return null;
        const cleaned = s.endsWith("%") ? s.slice(0, -1) : s;
        const n = Number(cleaned);
        return Number.isFinite(n) ? n : null;
      };
      const ensureOption = (pct: number | null): PercentageOption | null => {
        if (!pct) return null;
        const opt = PERCENTAGE_OPTIONS.find((o) => o.value === Math.round(pct));
        if (opt) return opt;
        return { label: `${Math.round(pct)}%`, value: Math.round(pct) };
      };

      const labourPctFromApi = pctFrom(pickPercentValue(h, ["labourChargesPercent"]));
      const lifterPctFromApi = pctFrom(pickPercentValue(h, ["lifterChargesPercent"]));

      const labourPctInferred = (() => {
        if (labourPctFromApi != null) return labourPctFromApi;
        const base = chargeBaseAmounts.labour;
        const amount = toNumber(h?.labourCharges ?? h?.labourCharge);
        if (!base || base <= 0) return null;
        const p = (amount / base) * 100;
        return Number.isFinite(p) ? Math.round(p) : null;
      })();
      const lifterPctInferred = (() => {
        if (lifterPctFromApi != null) return lifterPctFromApi;
        const base = chargeBaseAmounts.lifter;
        const amount = toNumber(h?.lifterCharges ?? h?.forkLifterCharge ?? h?.forkLifterCharges);
        if (!base || base <= 0) return null;
        const p = (amount / base) * 100;
        return Number.isFinite(p) ? Math.round(p) : null;
      })();

      const labourOpt = ensureOption(labourPctInferred) ?? PERCENTAGE_OPTIONS[3];
      const lifterOpt = ensureOption(lifterPctInferred) ?? PERCENTAGE_OPTIONS[3];
      setEditLabourPct(labourOpt);
      setEditLifterPct(lifterOpt);

      setEditLabourCharges(String(calcAmount(chargeBaseAmounts.labour, labourOpt.value)));
      setEditLifterCharges(String(calcAmount(chargeBaseAmounts.lifter, lifterOpt.value)));

      const lastNonEmptyRemarksInHistory = (): string => {
        if (!Array.isArray(history) || history.length === 0) return "";
        for (let i = history.length - 1; i >= 0; i -= 1) {
          const s = pickRemarks(history[i]);
          if (s) return s;
        }
        return "";
      };

      const remarks = pickRemarks(h) || lastNonEmptyRemarksInHistory() || pickRemarks(original);
      setEditRemarks(remarks);
      setEditOpen(true);
    };

    const deleteExam = async (h: any) => {
      const examId = h?.examId ?? h?.examID ?? h?.id;
      if (!examId) return;
      const ok = window.confirm("Are you sure you want to delete this examination entry?");
      if (!ok) return;

      try {
        setSaving(true);
        const res = await fetchWrapper.delete(
          `${DELETE_EXAM_ENDPOINT}/${encodeURIComponent(String(examId))}`
        );
        if (res?.isSuccess) {
          await runSearch({ silent: true });
        } else {
          dispatch(
            addNotificationAction({
              message: res?.error || res?.message || "Failed to delete examination.",
              options: { variant: "error" },
            })
          );
        }
      } catch (e: any) {
        dispatch(
          addNotificationAction({
            message: e?.message || "Failed to delete examination.",
            options: { variant: "error" },
          })
        );
      } finally {
        setSaving(false);
      }
    };

    return (
      <table className={gridClasses.tableNod}>
        <tr>
          <th className={gridClasses.tableHeadNod}>Sr.No</th>
          <th className={gridClasses.tableHeadNod}>Labour %</th>
          <th className={gridClasses.tableHeadNod}>Lifter %</th>
          <th className={gridClasses.tableHeadNod}>Labour</th>
          <th className={gridClasses.tableHeadNod}>Fork Lifter</th>
          <th className={gridClasses.tableHeadNod}>Exam Date</th>
          <th className={gridClasses.tableHeadNod}>Re-Exam Date</th>
          <th className={gridClasses.tableHeadNod}>Action</th>
        </tr>
        {history.map((h: any, idx: number) => (
          <tr key={h?.examId ?? idx}>
            <td className={gridClasses.tableHeadNod}>{idx + 1}</td>
            <td className={gridClasses.tableHeadNod}>
              {(() => {
                const apiValue = pickPercentValue(h, [
                  "labourChargesPercent",
                  "labourPercent",
                  "labourPercentage",
                ]);
                if (apiValue !== undefined && apiValue !== null && String(apiValue).trim() !== "") {
                  return percent(apiValue);
                }
                return percentFromBaseAmount(h, "labour") ?? "-";
              })()}
            </td>
            <td className={gridClasses.tableHeadNod}>
              {(() => {
                const apiValue = pickPercentValue(h, [
                  "lifterChargesPercent",
                  "forkLifterChargesPercent",
                  "forkLifterPercent",
                  "forkLifterPercentage",
                  "lifterPercent",
                  "lifterPercentage",
                ]);
                if (apiValue !== undefined && apiValue !== null && String(apiValue).trim() !== "") {
                  return percent(apiValue);
                }
                return percentFromBaseAmount(h, "lifter") ?? "-";
              })()}
            </td>
            <td className={gridClasses.tableHeadNod}>{formatRs(h?.labourCharges ?? "-")}</td>
            <td className={gridClasses.tableHeadNod}>{formatRs(h?.lifterCharges ?? "-")}</td>
            <td className={gridClasses.tableHeadNod}>
              {normalizeExamDate(h?.examDate) ? moment(h?.examDate).format("DD-MM-YYYY") : "-"}
            </td>
            <td className={gridClasses.tableHeadNod}>
              {normalizeExamDate(h?.reExamDate ?? h?.reExamOn)
                ? moment(h?.reExamDate ?? h?.reExamOn).format("DD-MM-YYYY")
                : "-"}
            </td>
            <td className={gridClasses.tableHeadNod}>
              <Box style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <Tooltip title="Edit">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => openEditDialog(h)}
                      disabled={saving}
                      aria-label="edit-examination"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => deleteExam(h)}
                      disabled={saving}
                      aria-label="delete-examination"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </td>
          </tr>
        ))}
      </table>
    );
  };

  const runSearch = async (opts?: { silent?: boolean }) => {
    if (!forwarderId) {
      setRows([]);
      return;
    }
    try {
      if (!opts?.silent) setLoading(true);
      const id = encodeURIComponent(String(Number(forwarderId)));
      const res = await fetchWrapper.get(`${CONTAINER_EXAM_ENDPOINT}/${id}`);
      if (res?.isSuccess) {
        const v = res?.value;
        const list =
          v?.containerExamination ||
          v?.records ||
          v?.list ||
          v?.data ||
          (Array.isArray(v) ? v : []) ||
          [];
        const mapped = (Array.isArray(list) ? list : []).map((r: any, idx: number) => {
          const parentRemarks =
            r?.remarks ??
            r?.remark ??
            r?.Remarks ??
            r?.Remark ??
            r?.remarksText ??
            r?.remarksNote ??
            r?.note ??
            r?.notes ??
            "";

          const historyWithRemarks = Array.isArray(r?.examinationHistory)
            ? r.examinationHistory.map((h: any) => {
                const examId = h?.examId ?? h?.examID ?? h?.id ?? null;
                const mappedRemarks =
                  examId != null ? String(remarksByExamId[String(examId)] ?? "").trim() : "";

                const hasRemarksKey =
                  h &&
                  typeof h === "object" &&
                  Object.keys(h).some(
                    (k) => /remark/i.test(k) && String(h?.[k] ?? "").trim() !== ""
                  );

                if (hasRemarksKey) return h;
                if (mappedRemarks) return { ...h, remarks: mappedRemarks };
                if (String(parentRemarks ?? "").trim() === "") return h;
                return { ...h, remarks: parentRemarks };
              })
            : r?.examinationHistory;

          const summed = sumHistoryCharges(historyWithRemarks);
          const exam = latestExaminationEntry(historyWithRemarks);

          const lifter =
            summed?.lifter ??
            (exam as any)?.lifterCharges ??
            (exam as any)?.forkLifterCharge ??
            r?.forkLifter ??
            r?.forkLifterCharge;
          const labourVal =
            summed?.labour ??
            (exam as any)?.labourCharges ??
            (exam as any)?.labourCharge ??
            r?.labour ??
            r?.labourCharge;

          const lifterNum = toNumber(lifter);
          const labourNum = toNumber(labourVal);
          const totalCharges = lifterNum + labourNum;
          return {
            ...r,
            examinationHistory: historyWithRemarks,
            id: r?.gatePassId ?? r?.id ?? r?.containerNo ?? r?.containerNumber ?? idx,
            serialNumber: idx + 1,
            containerNo: r?.containerNo ?? r?.containerNumber ?? r?.container_no ?? "",
            containerSize:
              r?.containerSize ??
              r?.container_size ??
              r?.size ??
              r?.containerType ??
              r?.container_type ??
              "",
            cargoType: r?.cargoType ?? "",
            importer: r?.importer ?? r?.importerName ?? r?.partyName ?? "",
            forkLifter:
              lifter !== undefined && lifter !== null && lifter !== ""
                ? lifter
                : r?.forkLifter ?? r?.forkLifterCharge ?? "",
            labour:
              labourVal !== undefined && labourVal !== null && labourVal !== ""
                ? labourVal
                : r?.labour ?? r?.labourCharge ?? "",
            totalCharges,
          };
        });
        setRows(mapped);
      } else {
        setRows([]);
      }
    } catch (e) {
      setRows([]);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!forwarderId) return;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    runSearch({ silent: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forwarderId]);

  const handleSave = async (payload: ExamDialogPayload) => {
    if (!payload?.containerNo) return;
    if (!dialogGatePassId) {
      dispatch(
        addNotificationAction({
          message: "GatePassId is missing for this container.",
          options: { variant: "error" },
        })
      );
      return;
    }
    if (chargeOptions.length === 0) {
      dispatch(
        addNotificationAction({
          message: "Please configure payment types (charges) in Configuration.",
          options: { variant: "error" },
        })
      );
      return;
    }

    const labourCharge = payload.charges.find((c) =>
      /labou?r/i.test(String(c.paymentTypeName || ""))
    );
    const lifterCharges = payload.charges.filter((c) => {
      const name = String(c.paymentTypeName || "").toLowerCase();
      return (name.includes("fork") || name.includes("lifter")) && !name.includes("labour");
    });
    const lifterChargeTotal = lifterCharges.reduce(
      (sum, c) => sum + toNumber(c?.calculatedAmount ?? 0),
      0
    );
    const lifterPct =
      lifterCharges.length > 0 &&
      lifterCharges.every((c) => String(c?.percentage) === String(lifterCharges[0]?.percentage))
        ? lifterCharges[0]?.percentage
        : "";

    const safeInt = (n: any) => {
      const v = Number(n ?? 0);
      if (!Number.isFinite(v)) return 0;
      return Math.round(v);
    };

    const selectedIso = moment(payload.date).toISOString();
    const emptyIso = "0001-01-01T00:00:00";
    const examIso =
      payload.mode === "re-exam"
        ? moment(dialogExistingExamDate || payload.date).toISOString()
        : selectedIso;
    const reExamIso = payload.mode === "re-exam" ? selectedIso : emptyIso;

    const body = {
      gatePassId: Number(dialogGatePassId),
      lifterChargesPercent: String(lifterPct ?? ""),
      labourChargesPercent: String(labourCharge?.percentage ?? ""),
      lifterCharges: safeInt(lifterChargeTotal),
      labourCharges: safeInt(labourCharge?.calculatedAmount),
      examDate: examIso,
      reExamDate: reExamIso,
      remarks: payload.remarks || "",
    };

    try {
      setSaving(true);
      const res = await fetchWrapper.post(CREATE_EXAM_ENDPOINT, body);
      if (res?.isSuccess) {
        setDialogOpen(false);
        await runSearch({ silent: true });
      } else {
        dispatch(
          addNotificationAction({
            message: res?.error || res?.message || "Failed to save examination.",
            options: { variant: "error" },
          })
        );
      }
    } catch (e: any) {
      dispatch(
        addNotificationAction({
          message: e?.message || "Failed to save examination.",
          options: { variant: "error" },
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedForwarder = useMemo(() => {
    if (!forwarderId) return null;
    return (partyRows || []).find((p: any) => String(p?.id) === String(forwarderId)) || null;
  }, [partyRows, forwarderId]);

  return (
    <>
      <Backdrop className={classes.backdrop} open={loading}>
        <CommonLoader />
      </Backdrop>

      <ExamDialog
        open={dialogOpen}
        mode={dialogMode}
        containerNo={dialogContainerNo}
        containerSize={dialogContainerSize}
        chargeOptions={chargeOptions}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        saving={saving}
      />

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Update Examination</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="GatePassId"
                value={editGatePassId ?? ""}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="ExamId"
                value={editExamId ?? ""}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Exam Date"
                type="datetime-local"
                value={editExamDate}
                onChange={(e) => setEditExamDate(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Re-Exam Date"
                type="datetime-local"
                value={editReExamDate}
                onChange={(e) => setEditReExamDate(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CommonAutoCompleteField
                cssClassName="customTextField"
                label="Lifter %"
                placeholder="Select %"
                name="editLifterPct"
                disableClearable={true}
                optionsType={PERCENTAGE_OPTIONS}
                getOptionLabel={(o: any) => o?.label || ""}
                value={editLifterPct}
                onChange={(o: any) => {
                  const opt = (o as any) || null;
                  setEditLifterPct(opt);
                  const pct = Number(opt?.value ?? 0);
                  setEditLifterCharges(String(calcAmount(chargeBaseAmounts.lifter, pct)));
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CommonAutoCompleteField
                cssClassName="customTextField"
                label="Labour %"
                placeholder="Select %"
                name="editLabourPct"
                disableClearable={true}
                optionsType={PERCENTAGE_OPTIONS}
                getOptionLabel={(o: any) => o?.label || ""}
                value={editLabourPct}
                onChange={(o: any) => {
                  const opt = (o as any) || null;
                  setEditLabourPct(opt);
                  const pct = Number(opt?.value ?? 0);
                  setEditLabourCharges(String(calcAmount(chargeBaseAmounts.labour, pct)));
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Lifter Charges"
                value={editLifterCharges}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Labour Charges"
                value={editLabourCharges}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Remarks"
                value={editRemarks}
                onChange={(e) => setEditRemarks(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          <Button onClick={() => setEditOpen(false)} variant="outlined" disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!editGatePassId || !editExamId) return;
              try {
                setSaving(true);
                const emptyIso = "0001-01-01T00:00:00";
                const examIso = editExamDate ? moment(editExamDate).toISOString() : emptyIso;
                const reExamIso = editReExamDate ? moment(editReExamDate).toISOString() : emptyIso;
                const body = {
                  gatePassId: Number(editGatePassId),
                  examId: Number(editExamId),
                  lifterChargesPercent: String(editLifterPct?.value ?? ""),
                  labourChargesPercent: String(editLabourPct?.value ?? ""),
                  lifterCharges: Number(editLifterCharges ?? 0),
                  labourCharges: Number(editLabourCharges ?? 0),
                  examDate: examIso,
                  reExamDate: reExamIso,
                  remarks: String(editRemarks ?? ""),
                };

                const res = await fetchWrapper.put(UPDATE_EXAM_ENDPOINT, body);
                if (res?.isSuccess) {
                  setRemarksByExamId((prev) => ({
                    ...prev,
                    [String(editExamId)]: String(editRemarks ?? "").trim(),
                  }));
                  setEditOpen(false);
                  await runSearch({ silent: true });
                } else {
                  dispatch(
                    addNotificationAction({
                      message: res?.error || res?.message || "Failed to update examination.",
                      options: { variant: "error" },
                    })
                  );
                }
              } catch (e: any) {
                dispatch(
                  addNotificationAction({
                    message: e?.message || "Failed to update examination.",
                    options: { variant: "error" },
                  })
                );
              } finally {
                setSaving(false);
              }
            }}
            color="primary"
            variant="contained"
            disabled={saving || !editGatePassId || !editExamId}
            style={{ backgroundColor: "#72CA00" }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <TabHeader module={moduleName} heading={pageHeading} />
      <Box m={2} />

      <Card>
        <CardContent className={classes.cardMain} style={{ height: "auto", paddingTop: 16 }}>
          <Box mt={1}>
            <Grid container spacing={2} direction="row" alignItems="center">
              <Grid item xs={12} md={4} lg={3}>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Forwarder List"
                  placeholder="Select Forwarder"
                  name="forwarderId"
                  disableClearable={true}
                  optionsType={partyRows}
                  getOptionLabel={(o: any) => o?.name || ""}
                  value={selectedForwarder}
                  onChange={(o: any) => setForwarderId(String(o?.id || ""))}
                />
              </Grid>
            </Grid>
          </Box>

          <Box mt={2} />
          <Divider />
          <Box mt={2} />

          <CommonMaterialGrid
            rows={rows}
            columns={columns}
            loader={loading}
            defaultPerPageRecord={15}
            height={550}
            enableExpanding
            renderDetailPanel={detailPanel}
            getRowCanExpand={({ original }: any) =>
              Number(original?.isExamDone ?? 0) === 1 &&
              Array.isArray(original?.examinationHistory) &&
              original.examinationHistory.length > 0
            }
          />
        </CardContent>
      </Card>
    </>
  );
}
